import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PListDetailsDto, PListLineDto, PalletSummaryDto } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';
import { settingsService } from '../../../../../services/settingsService';
import { itemsService } from '../../../../../services/itemsService';
import { plistOrderService, PListOrderDto } from '../../../../../services/plistOrderService';
import { userProfileService } from '../../../../../services/userProfileService';
import { UserProfileDto } from '../../../../../types/userProfile';
import { PickingLine, GS1Data } from '../types';
import { getPalletAllocationsForLine, countRegisteredBatches } from '../palletHelpers';

// Extended picking line with additional UI properties
export interface ExtendedPickingLine extends PListLineDto {
  isPartiallyPicked?: boolean;
  isFullyPicked?: boolean;
  image?: string;
  requireLocationScan?: boolean;
  requireItemScan?: boolean;
  palletId?: string; // Now refers to SSCC
  itemDescription?: string;
  location?: string;
}

export interface UsePickingDataReturn {
  // Core data
  pickingDetails: PListDetailsDto | null;
  pickingLines: ExtendedPickingLine[];
  setPickingLines: React.Dispatch<React.SetStateAction<ExtendedPickingLine[]>>;
  loading: boolean;
  error: string | null;
  userProfile: UserProfileDto | null;
  pListOrders: PListOrderDto[];
  setPListOrders: React.Dispatch<React.SetStateAction<PListOrderDto[]>>;

  // Line tracking
  activeLineIndex: number | null;
  setActiveLineIndex: React.Dispatch<React.SetStateAction<number | null>>;
  currentLineIndex: number | null;
  setCurrentLineIndex: React.Dispatch<React.SetStateAction<number | null>>;
  lineStartTimes: Map<number, string>;
  setLineStartTimes: React.Dispatch<React.SetStateAction<Map<number, string>>>;

  // Computed values
  activeLine: ExtendedPickingLine | null;
  currentLine: ExtendedPickingLine | null;
  completedLines: number;

  // Settings
  enableRapidScanning: boolean;
  setEnableRapidScanning: React.Dispatch<React.SetStateAction<boolean>>;
  autoShowImage: boolean;
  setAutoShowImage: React.Dispatch<React.SetStateAction<boolean>>;
  enableDragAndDrop: boolean;
  setEnableDragAndDrop: React.Dispatch<React.SetStateAction<boolean>>;
  zeroPointCountingEnabled: boolean;
  fewPointCountingThreshold: number;

  // Data loading functions
  // focusPListLineNo: behold aktiv linje på denne plukklinjen etter reload (brukes av
  // telle-snarveien i strukturplukk — operatøren står fysisk på lokasjonen han nettopp telte).
  loadPickingDetails: (profile?: UserProfileDto | null, focusPListLineNo?: number) => Promise<void>;
  loadPListOrders: () => Promise<void>;
  loadUserProfile: () => Promise<UserProfileDto | null>;

  // Helper functions
  reorganizeLines: (lines: ExtendedPickingLine[]) => ExtendedPickingLine[];
  findLineByItem: (itemNo: string) => ExtendedPickingLine | null;
  convertToPickingLine: (line: ExtendedPickingLine) => PickingLine;
  convertToPickingLineWithCheck: (line: ExtendedPickingLine | undefined) => PickingLine | null;
  getOrderLineFieldValue: (line: ExtendedPickingLine, fieldName?: string) => string | undefined;
  parseGS1Barcode: (barcode: string) => GS1Data;
}

export function usePickingData(
  pListId: number,
  palletSummaries: PalletSummaryDto[],
  /**
   * D-pak-modus (innstillingen picking.autoCreatePalletOnDPakScan). Styrer to ting som
   * ellers ville endret oppførsel for ALLE kunder: hvor bredt paller matches mot linjer,
   * og om batch telles fra OrderSSCC i tillegg til OrderBatchNo. Av => uendret oppførsel.
   */
  dpakMode: boolean = false
): UsePickingDataReturn {
  // Core data state
  const [pickingDetails, setPickingDetails] = useState<PListDetailsDto | null>(null);
  const [pickingLines, setPickingLines] = useState<ExtendedPickingLine[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState<number | null>(null);
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lineStartTimes, setLineStartTimes] = useState<Map<number, string>>(new Map());
  const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);
  const [pListOrders, setPListOrders] = useState<PListOrderDto[]>([]);

  // Settings
  const [enableRapidScanning, setEnableRapidScanning] = useState(false);
  const [autoShowImage, setAutoShowImage] = useState(false);
  const [enableDragAndDrop, setEnableDragAndDrop] = useState(false);
  const [zeroPointCountingEnabled, setZeroPointCountingEnabled] = useState(true);
  const [fewPointCountingThreshold, setFewPointCountingThreshold] = useState(0);

  // Computed values
  const activeLine = activeLineIndex !== null ? pickingLines[activeLineIndex] : null;
  const currentLine = currentLineIndex !== null ? pickingLines[currentLineIndex] : null;
  const completedLines = pickingLines.filter(line => line.isCompleted).length;

  // ─── Varebilde ved plukk (36334101) ───
  // items.imageBaseUrl er master-gaten: tom/usatt = ingen bilder (dagens oppførsel).
  // items.imageNameField peker på feltet som holder bildefilnavnet (default
  // ItemPictureName; OrderLineFreeField1-10 støttes også). Ref-basert så
  // loadPickingDetails slipper settings som dependency.
  const imageConfigRef = useRef<{ baseUrl: string; field: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [baseUrlSetting, fieldSetting] = await Promise.all([
          settingsService.getEffectiveSetting('items.imageBaseUrl'),
          settingsService.getEffectiveSetting('items.imageNameField')
        ]);
        if (cancelled) return;
        const baseUrl = (baseUrlSetting?.currentValue || '').toString().trim();
        const field = (fieldSetting?.currentValue || '').toString().trim() || 'ItemPictureName';
        imageConfigRef.current = baseUrl ? { baseUrl: baseUrl.replace(/\/+$/, ''), field } : null;
      } catch (err) {
        console.warn('Could not load item image settings (items.imageBaseUrl/imageNameField):', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * URL til varebildet for en plukklinje, eller undefined når funksjonen er av
   * (ingen base-URL), operatøren har slått bilder eksplisitt av
   * (ShowItemPicureYesNo=false — null/true betyr vis), eller navnefeltet er tomt.
   */
  const resolveItemImage = (line: PListLineDto, profile: UserProfileDto | null): string | undefined => {
    const cfg = imageConfigRef.current;
    if (!cfg) return undefined;
    if (profile?.showItemPicureYesNo === false) return undefined;

    const field = cfg.field.toLowerCase();
    let name: string | undefined;
    if (field === 'itempicturename') {
      name = line.orderLineItemPictureName;
    } else {
      const freeFieldMatch = field.match(/^orderlinefreefield(10|[1-9])$/);
      if (freeFieldMatch) {
        name = (line as unknown as Record<string, string | undefined>)[`orderLineFreeField${freeFieldMatch[1]}`];
      } else {
        // Samme valider-med-advarsel-mønster som printing.myLocOrderField
        console.warn(`items.imageNameField='${cfg.field}' støttes ikke i plukk — bruk ItemPictureName eller OrderLineFreeField1-10`);
        return undefined;
      }
    }

    const trimmed = name?.trim();
    if (!trimmed) return undefined;
    return `${cfg.baseUrl}/${encodeURIComponent(trimmed)}`;
  };

  // ─────────────────────────────────────────────────
  // Data Loading
  // ─────────────────────────────────────────────────

  const loadUserProfile = useCallback(async (): Promise<UserProfileDto | null> => {
    try {
      const profile = await userProfileService.getCurrentUserProfile();
      setUserProfile(profile);
      console.log('User profile loaded:', {
        barCodeConfirmItem: profile.barCodeConfirmItem,
        barCodeConfirmLocation: profile.barCodeConfirmLocation
      });
      return profile;
    } catch (error) {
      console.warn('Could not load user profile, using default barcode settings:', error);
      return null;
    }
  }, []);

  const loadPickingDetails = useCallback(async (profile?: UserProfileDto | null, focusPListLineNo?: number) => {
    try {
      setLoading(true);
      setError(null);
      const details = await pickingService.getPickingDetails(pListId);
      setPickingDetails(details);

      // Fetch item descriptions from Items table
      const itemDescriptions = new Map<string, string>();
      const itemNos = details.lines.map(line => line.itemNo).filter(Boolean) as string[];
      const uniqueItemNos = Array.from(new Set(itemNos));

      for (const itemNo of uniqueItemNos) {
        try {
          const item = await itemsService.getItemByCompanyAndItemNo(details.header.companyNo, itemNo!);
          if (item.description1) {
            itemDescriptions.set(itemNo!, item.description1);
          }
        } catch (error) {
          console.warn(`Could not fetch description for item ${itemNo}:`, error);
        }
      }

      // Convert to ExtendedPickingLine format with additional properties
      const effectiveProfile = profile !== undefined ? profile : userProfile;
      const requireLocationScan = effectiveProfile?.barCodeConfirmLocation ?? false;
      const requireItemScan = effectiveProfile?.barCodeConfirmItem ?? false;

      const lines: ExtendedPickingLine[] = details.lines.map((line) => ({
        ...line,
        isCompleted: line.qtyPicked > 0,
        isPartiallyPicked: line.qtyPicked > 0 && line.qtyPicked < line.qty,
        isFullyPicked: line.qtyPicked >= line.qty,
        // Ikke-lagerstyrte varer (Items.StcTransactionsYesNo=false): ingen fysisk saldo å
        // verifisere, så hopp over krav om lokasjonsbekreftelse. Operatøren taster fortsatt
        // antall og ferdigmelder (Ariens-tilpasning).
        requireLocationScan: line.stcTransactionsYesNo === false ? false : requireLocationScan,
        requireItemScan: requireItemScan,
        // 36334101: ekte varebilde fra items.imageBaseUrl + navnefelt (før: demo-plassholder)
        image: resolveItemImage(line, effectiveProfile ?? null),
        // Ordrelinjeteksten fra ERP (kan være redigert på ordren, f.eks. 07/08-tekstlinjer)
        // har forrang over produktkortets beskrivelse — som i BCL og i pakking
        // (PackingRepository.GetOrderLinesAsync bruker samme COALESCE-prioritering).
        itemDescription: line.orderLineDescription1?.trim()
          || (line.itemNo ? (itemDescriptions.get(line.itemNo) || 'Ingen beskrivelse') : 'Ukjent vare'),
        // K5: Prefer human-readable bin description (StcBin.Description1) over raw BinCode for display.
        // IMPORTANT: This is a DISPLAY-ONLY field. Barcode validation and any API call requiring
        // a real binCode (mandatoryCountingService, ItemBins lookup, SSCC locationName, [X] checks)
        // MUST use line.binCode directly, not line.location.
        // Strukturvare-hovedvarer (gruppehoder) har bevisst ingen lokasjon — ikke vis placeholder.
        location: (line.structureLineType ?? 0) === 1
          ? ''
          : (line.binDescription1 || line.binCode || 'A-01-01'),
        orderNoERP: line.orderNoERP,
        customerName: line.customerName
      }));

      // Sort by Sorting field (ascending)
      const sortedLines = lines.sort((a, b) => a.sorting - b.sorting);

      // Reorganiser: ufullførte øverst, fullførte i midten, manko ([X]) nederst (Ariens).
      const reorganized = reorganizeLines(sortedLines);
      setPickingLines(reorganized);
      // Aktiv linje: hvis kalleren ba om fokus på en bestemt linje (f.eks. etter telling —
      // operatøren står fysisk på den lokasjonen), behold den som aktiv så lenge den
      // fortsatt er plukkbar. Ellers: første handlingsbare (ufullført, ikke manko, ikke
      // struktur-gruppehode).
      const focusIndex = focusPListLineNo != null
        ? reorganized.findIndex(line =>
            line.pListLineNo === focusPListLineNo &&
            !line.isFullyPicked && line.binCode !== '[X]' && (line.structureLineType ?? 0) !== 1)
        : -1;
      if (focusIndex !== -1) {
        setActiveLineIndex(focusIndex);
        setCurrentLineIndex(focusIndex);
      } else {
        const firstActionable = reorganized.findIndex(line =>
          !line.isCompleted && line.binCode !== '[X]' && (line.structureLineType ?? 0) !== 1);
        setActiveLineIndex(firstActionable !== -1 ? firstActionable : null);
      }
    } catch (err) {
      console.error('Error loading picking details:', err);
      setError('Kunne ikke laste plukk-detaljer. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }, [pListId, userProfile]);

  const loadPListOrders = useCallback(async () => {
    try {
      const companyNo = pickingDetails!.header.companyNo;
      console.log('Loading PListOrders for PListID:', pListId, 'CompanyNo:', companyNo);
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      console.log('Loaded PListOrders:', orders);
      setPListOrders(orders);
    } catch (error) {
      console.error('Error loading PListOrders:', error);
    }
  }, [pListId, pickingDetails]);

  // ─────────────────────────────────────────────────
  // Initial data load + settings
  // ─────────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      // Load rapid scanning setting (Profile -> System -> Default)
      try {
        const fastPickSetting = await settingsService.getEffectiveSetting('picking.fastpick');
        if (fastPickSetting?.currentValue) {
          const isEnabled = fastPickSetting.currentValue === 'true' || fastPickSetting.currentValue === '1';
          setEnableRapidScanning(isEnabled);
          console.log('Loaded picking.fastpick effective setting:', isEnabled, '(source:', fastPickSetting.effectiveSource + ')');
        }
      } catch (err) {
        console.warn('Could not load picking.fastpick setting, using default (false):', err);
      }

      // Load zero-point counting setting (system level)
      try {
        const zeroCountSetting = await settingsService.getEffectiveSetting('picking.ZeroCountOnOff');
        if (zeroCountSetting?.currentValue) {
          const isEnabled = zeroCountSetting.currentValue === 'true' || zeroCountSetting.currentValue === '1';
          setZeroPointCountingEnabled(isEnabled);
          console.log('Loaded picking.ZeroCountOnOff effective setting:', isEnabled, '(source:', zeroCountSetting.effectiveSource + ')');
        }
      } catch (err) {
        console.warn('Could not load picking.ZeroCountOnOff setting, using default (true):', err);
      }

      // Load few-point counting threshold setting (system level)
      try {
        const fewPointSetting = await settingsService.getEffectiveSetting('picking.FewPointCountingThreshold');
        if (fewPointSetting?.currentValue) {
          const threshold = parseInt(fewPointSetting.currentValue, 10);
          if (!isNaN(threshold) && threshold >= 0) {
            setFewPointCountingThreshold(threshold);
            console.log('Loaded picking.FewPointCountingThreshold effective setting:', threshold, '(source:', fewPointSetting.effectiveSource + ')');
          }
        }
      } catch (err) {
        console.warn('Could not load picking.FewPointCountingThreshold setting, using default (0):', err);
      }

      const profile = await loadUserProfile();
      await loadPickingDetails(profile);
    };
    loadData();
  }, [pListId]);

  // Load PListOrders after pickingDetails is loaded
  useEffect(() => {
    if (pickingDetails) {
      loadPListOrders();
    }
  }, [pickingDetails]);

  // Track StartDateTime when active line changes
  useEffect(() => {
    if (activeLineIndex === null || pickingLines.length === 0) {
      return;
    }

    const currentActiveLine = pickingLines[activeLineIndex];
    if (currentActiveLine) {
      const pListLineNo = currentActiveLine.pListLineNo;
      setLineStartTimes(prev => {
        const updated = new Map(prev);
        updated.set(pListLineNo, new Date().toISOString());
        return updated;
      });
      console.log(`StartDateTime set for line ${pListLineNo}:`, new Date().toISOString());
    }
  }, [activeLineIndex, pickingLines]);

  // ─────────────────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────────────────

  const reorganizeLines = useCallback((lines: ExtendedPickingLine[]): ExtendedPickingLine[] => {
    let sortedLines = [...lines].sort((a, b) => a.sorting - b.sorting);

    // Strukturvarer: speil backendens avledning av hovedvarens plukkede antall
    // (min over komponentene av floor(plukket / antall pr. sett), begrenset av bestilt).
    // Frontend-state oppdaterer ellers bare den plukkede linjen — uten dette viser
    // aktiv-kortet og avslutt-avviksmodalen 0 på hovedvaren til bildet reloades.
    sortedLines = sortedLines.map(line => {
      if ((line.structureLineType ?? 0) !== 1) return line;

      const components = sortedLines.filter(l =>
        (l.structureLineType ?? 0) === 2 &&
        l.orderNo === line.orderNo &&
        l.structureParentLineNoERP != null &&
        l.structureParentLineNoERP === line.lineNoERP);
      if (components.length === 0) return line;

      // Per komponent-ordrelinje (kan ha flere plukklinjer): summer plukket
      const byComponent = new Map<number, { perUnit: number; picked: number }>();
      components.forEach(c => {
        const entry = byComponent.get(c.lineNo) ?? { perUnit: 0, picked: 0 };
        if (!entry.perUnit && c.structureQtyPerUnit && c.structureQtyPerUnit > 0) {
          entry.perUnit = c.structureQtyPerUnit;
        }
        entry.picked += c.qtyPicked;
        byComponent.set(c.lineNo, entry);
      });

      let sets: number | null = null;
      byComponent.forEach(entry => {
        if (entry.perUnit > 0) {
          const s = Math.floor(entry.picked / entry.perUnit);
          sets = sets === null ? s : Math.min(sets, s);
        }
      });
      if (sets === null) return line;

      const derivedQtyPicked = Math.min(sets, line.qty);
      if (derivedQtyPicked === line.qtyPicked) return line;

      return {
        ...line,
        qtyPicked: derivedQtyPicked,
        isCompleted: derivedQtyPicked > 0,
        isPartiallyPicked: derivedQtyPicked > 0 && derivedQtyPicked < line.qty,
        isFullyPicked: derivedQtyPicked >= line.qty
      };
    });

    // Strukturvarer: hovedvare (gruppehode) + komponentene dens holdes ALLTID samlet som én
    // blokk — også når komponenter er delplukket eller har manko ([X]). Vanlige linjer
    // beholder eksisterende oppførsel: ufullførte øverst, fullførte i midten, manko nederst.
    const blocks: ExtendedPickingLine[][] = [];
    const used = new Set<number>();

    for (const line of sortedLines) {
      if (used.has(line.pListLineNo)) continue;

      if ((line.structureLineType ?? 0) === 1) {
        const components = sortedLines.filter(l =>
          (l.structureLineType ?? 0) === 2 &&
          l.orderNo === line.orderNo &&
          l.structureParentLineNoERP != null &&
          l.structureParentLineNoERP === line.lineNoERP);
        // Operatøren skal starte med den BEGRENSENDE komponenten (minst dekning i antall
        // sett) — avviket mot ERP-saldo bør sjekkes fysisk før resten av settet plukkes.
        // Full dekning => alle like => stabil sort beholder opprinnelig rekkefølge.
        const headerQty = line.qty;
        const setsCoverable = (c: ExtendedPickingLine): number => {
          const perUnit = c.structureQtyPerUnit ?? 0;
          if (perUnit <= 0) return Number.POSITIVE_INFINITY;
          const covered = Math.min(c.qty, (c.qtyPicked ?? 0) + Math.max(0, c.binAvailableQty ?? 0));
          return Math.min(Math.floor(covered / perUnit), headerQty);
        };
        const orderedComponents = [...components].sort((a, b) => setsCoverable(a) - setsCoverable(b));
        const block = [line, ...orderedComponents];
        block.forEach(l => used.add(l.pListLineNo));
        blocks.push(block);
      } else {
        used.add(line.pListLineNo);
        blocks.push([line]);
      }
    }

    // En blokk regnes som fullført når alle plukkbare linjer i den er fullført
    // (hovedvare-headeren og [X]-linjer teller ikke som plukkbare).
    const isBlockCompleted = (block: ExtendedPickingLine[]) => {
      const pickable = block.filter(l => (l.structureLineType ?? 0) !== 1 && l.binCode !== '[X]');
      return pickable.length > 0 && pickable.every(l => l.isCompleted);
    };
    // Rene manko-blokker (enkeltlinje med [X], utenom strukturkomponenter) samles nederst.
    const isShortageBlock = (block: ExtendedPickingLine[]) =>
      block.length === 1 && block[0].binCode === '[X]';

    const shortage = blocks.filter(isShortageBlock);
    const rest = blocks.filter(b => !isShortageBlock(b));
    const completed = rest.filter(isBlockCompleted);
    const uncompleted = rest.filter(b => !isBlockCompleted(b));

    return [...uncompleted.flat(), ...completed.flat(), ...shortage.flat()];
  }, []);

  const findLineByItem = useCallback((itemNo: string): ExtendedPickingLine | null => {
    const itemNoLower = itemNo.toLowerCase();
    const cleanedItemNo = itemNo.replace(/^0+/, '') || itemNo;
    const cleanedItemNoLower = cleanedItemNo.toLowerCase();

    const matches = (line: ExtendedPickingLine): boolean => {
      // Strukturvare-hovedvarer (gruppehoder) plukkes aldri — skann av hovedvarens
      // strekkode skal ikke treffe headerlinjen.
      if ((line.structureLineType ?? 0) === 1) {
        return false;
      }
      const lineItemNo = line.itemNo?.toLowerCase();
      const lineBarCode = line.itemBarCode?.toLowerCase();

      if (lineItemNo && (lineItemNo === itemNoLower || lineItemNo === cleanedItemNoLower)) {
        return true;
      }
      if (lineBarCode && (lineBarCode === itemNoLower || lineBarCode === cleanedItemNoLower)) {
        return true;
      }
      return false;
    };

    const incompleteLine = pickingLines.find(line => matches(line) && !line.isCompleted);
    if (incompleteLine) {
      return incompleteLine;
    }

    return pickingLines.find(line => matches(line)) || null;
  }, [pickingLines]);

  const parseGS1Barcode = useCallback((barcode: string): GS1Data => {
    const gs1Data: GS1Data = {};

    const patterns = {
      sscc: /\(00\)(\d{18})/,
      itemNo: /\(01\)([^()]+?)(?=\(|$)/,
      quantity: /\(30\)(\d+)/,
      batch: /\(10\)([^()]+?)(?=\(|$)/,
      expiry: /\(17\)(\d{6})/
    };

    Object.entries(patterns).forEach(([key, pattern]) => {
      const match = barcode.match(pattern);
      if (match) {
        if (key === 'quantity') {
          gs1Data[key] = parseInt(match[1]);
        } else if (key === 'itemNo') {
          const gtin = match[1];
          const varMatch = gtin.match(/VAR\d+/);
          if (varMatch) {
            gs1Data[key] = varMatch[0];
          } else {
            const trimmed = gtin.replace(/^0+/, '') || gtin.slice(-6);
            gs1Data[key] = trimmed;
          }
        } else {
          gs1Data[key as keyof GS1Data] = match[1] as any;
        }
      }
    });

    return gs1Data;
  }, []);

  // Lookup OrderLine field value by field name from UserProfile settings
  const getOrderLineFieldValue = useCallback((line: ExtendedPickingLine, fieldName?: string): string | undefined => {
    if (!fieldName) return undefined;
    const normalized = fieldName.toLowerCase().trim();
    const fieldMap: Record<string, string | undefined> = {
      'orderlinefreefield1': line.orderLineFreeField1,
      'orderlinefreefield2': line.orderLineFreeField2,
      'orderlinefreefield3': line.orderLineFreeField3,
      'orderlinefreefield4': line.orderLineFreeField4,
      'orderlinefreefield5': line.orderLineFreeField5,
      'orderlinefreefield6': line.orderLineFreeField6,
      'orderlinefreefield7': line.orderLineFreeField7,
      'orderlinefreefield8': line.orderLineFreeField8,
      'orderlinefreefield9': line.orderLineFreeField9,
      'orderlinefreefield10': line.orderLineFreeField10,
      'description1': line.orderLineDescription1,
      'operatortxt': line.orderLineOperatorTxt,
      'batchid': line.orderLineBatchID,
      'buyersitemno': line.orderLineBuyersItemNo,
      'itemgroup1': line.orderLineItemGroup1,
      'itemgroup2': line.orderLineItemGroup2,
      'itemgroup3': line.orderLineItemGroup3,
      'itemtemperatureinfo': line.orderLineItemTemperatureInfo,
      'uncode': line.orderLineUNCode,
      'memo1': line.orderLineMemo1,
      'itembarcode': line.itemBarCode,
      'serialno': line.orderLineSerialNo,
    };
    return fieldMap[normalized];
  }, []);

  // Convert ExtendedPickingLine to PickingLine for child components
  const convertToPickingLine = useCallback((line: ExtendedPickingLine): PickingLine => {
    // Felles oppslag (palletHelpers). Med dpakMode=false brukes nøyaktig samme predikat
    // som før D-pak-arbeidet, så eksisterende kunder ser uendret resultat.
    const allocations = getPalletAllocationsForLine(palletSummaries, line, dpakMode);
    const splitAllocations = allocations.map(allocation => ({
      palletId: allocation.sscc,
      qty: allocation.quantity,
      itemBatchNo: allocation.itemBatchNo,
      itemBatchExpDate: allocation.itemBatchExpDate,
      gtin: allocation.gtin,
    }));

    return {
      pListLineNo: line.pListLineNo,
      itemNo: line.itemNo || '',
      itemDescription: line.itemDescription || `${line.itemNo || 'Unknown'} - Beskrivelse`,
      qty: line.qty,
      qtyPicked: line.qtyPicked,
      qtyReserved: line.qtyReserved,
      location: line.location || line.binDescription1 || line.binCode || '',
      binCode: line.binCode,
      orderNo: line.orderNo.toString(),
      lineNo: line.lineNo,
      orderNoERP: line.orderNoERP,
      customerName: line.customerName,
      isCompleted: line.isCompleted,
      isPartiallyPicked: line.isPartiallyPicked,
      isFullyPicked: line.isFullyPicked,
      image: line.image,
      requireLocationScan: line.requireLocationScan,
      requireItemScan: line.requireItemScan,
      stcTransactionsYesNo: line.stcTransactionsYesNo,
      palletId: line.palletId,
      splitAllocations: splitAllocations.length > 0 ? splitAllocations : undefined,
      // Item type and Serial/Batch tracking
      itemType: line.itemType,
      serialNoItemYesNo: line.serialNoItemYesNo,
      batchItemYesNo: line.batchItemYesNo,
      // Strukturvarer: gruppehode/komponent-kobling for gruppert visning
      lineNoERP: line.lineNoERP,
      structureLineType: line.structureLineType,
      structureParentLineNoERP: line.structureParentLineNoERP,
      structureQtyPerUnit: line.structureQtyPerUnit,
      binAvailableQty: line.binAvailableQty,
      serialNumberCount: line.serialNumbers?.length || 0,
      // I D-pak-modus telles batch fra BEGGE kilder: OrderBatchNo (manuell registrering)
      // og OrderSSCC.ItemBatchNo (etiketten). Ellers uendret: kun OrderBatchNo, slik at
      // badge og varsel oppfører seg nøyaktig som før for eksisterende kunder.
      batchNumberCount: dpakMode
        ? countRegisteredBatches(palletSummaries, line, line.batchNumbers, true)
        : (line.batchNumbers?.length || 0),
      extraInfoField2Value: getOrderLineFieldValue(line, userProfile?.userdefinedExtraInfoField2),
      extraInfoField3Value: getOrderLineFieldValue(line, userProfile?.userdefinedExtraInfoField3),
    };
  }, [palletSummaries, userProfile, getOrderLineFieldValue, dpakMode]);

  const convertToPickingLineWithCheck = useCallback((line: ExtendedPickingLine | undefined): PickingLine | null => {
    if (!line) return null;
    return convertToPickingLine(line);
  }, [convertToPickingLine]);

  return {
    // Core data
    pickingDetails,
    pickingLines,
    setPickingLines,
    loading,
    error,
    userProfile,
    pListOrders,
    setPListOrders,

    // Line tracking
    activeLineIndex,
    setActiveLineIndex,
    currentLineIndex,
    setCurrentLineIndex,
    lineStartTimes,
    setLineStartTimes,

    // Computed values
    activeLine,
    currentLine,
    completedLines,

    // Settings
    enableRapidScanning,
    setEnableRapidScanning,
    autoShowImage,
    setAutoShowImage,
    enableDragAndDrop,
    setEnableDragAndDrop,
    zeroPointCountingEnabled,
    fewPointCountingThreshold,

    // Data loading functions
    loadPickingDetails,
    loadPListOrders,
    loadUserProfile,

    // Helper functions
    reorganizeLines,
    findLineByItem,
    convertToPickingLine,
    convertToPickingLineWithCheck,
    getOrderLineFieldValue,
    parseGS1Barcode,
  };
}
